import React, { useEffect, useRef, useState } from 'react';
import api, { proxyImage } from '../services/api';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface ParseResult {
  platform: string;
  uid: string;
  name: string;
  face: string;
}

const PLATFORM_LABEL: Record<string, string> = {
  bilibili: 'B站',
  youtube: 'YouTube',
};

export default function DataSourceRequestModal({ open, onClose }: Props) {
  const [url, setUrl] = useState('');
  const [reason, setReason] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [parseError, setParseError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const timerRef = useRef<number | null>(null);

  // 打开时重置表单状态
  useEffect(() => {
    if (!open) return;
    setUrl('');
    setReason('');
    setParseResult(null);
    setParseError('');
    setMsg('');
    setSubmitLoading(false);
  }, [open]);

  // 输入 url 后防抖 600ms 自动解析
  useEffect(() => {
    if (!open) return;
    const u = url.trim();
    if (!u) {
      setParsing(false);
      setParseResult(null);
      setParseError('');
      return;
    }
    setParsing(true);
    setParseError('');
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(async () => {
      try {
        const res = await api.parseDataSource(u);
        setParseResult(res.data?.data || null);
      } catch (err: any) {
        setParseError(err?.response?.data?.detail || '解析失败，请检查链接格式');
        setParseResult(null);
      } finally {
        setParsing(false);
      }
    }, 600);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [url, open]);

  const handleSubmit = async () => {
    const u = url.trim();
    if (!u || !parseResult || submitLoading) return;
    setSubmitLoading(true);
    setMsg('');
    try {
      await api.createDataSourceRequest({
        name: parseResult.name || '未命名',
        url: u,
        reason: reason.trim(),
      });
      setMsg('✅ 数据源添加成功，正在后台同步视频...');
      setTimeout(onClose, 1200);
    } catch (err: any) {
      setMsg('⚠ ' + (err?.response?.data?.detail || '提交失败，请稍后重试'));
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>添加数据源</h3>
          <button className="modal-close" onClick={onClose} title="关闭">✕</button>
        </div>
        <div className="modal-body">
          <label className="dsr-label">粘贴链接（B站空间 / YouTube 频道）</label>
          <input
            className="input dsr-input"
            placeholder="https://space.bilibili.com/xxx 或 https://www.youtube.com/channel/UCxxx"
            value={url}
            onChange={e => setUrl(e.target.value)}
            autoFocus
          />

          {parsing && <div className="dsr-status">⏳ 正在解析链接...</div>}
          {parseError && <div className="dsr-error">⚠ {parseError}</div>}
          {parseResult && (
            <div className="dsr-preview">
              <span className={`dsr-platform-badge dsr-platform-${parseResult.platform}`}>
                {PLATFORM_LABEL[parseResult.platform] || parseResult.platform}
              </span>
              <img
                loading="lazy"
                src={proxyImage(parseResult.face, 48, 48)}
                alt=""
                className="dsr-avatar"
              />
              <div className="dsr-preview-main">
                <span className="dsr-name">{parseResult.name || '未解析出名字'}</span>
                <span className="dsr-uid">{parseResult.uid}</span>
              </div>
              <span className="dsr-valid">✓ 有效</span>
            </div>
          )}

          <label className="dsr-label">申请理由（选填）</label>
          <textarea
            className="input dsr-textarea"
            placeholder="例如：想看这位博主的增肌训练干货"
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
          />

          {msg && <div className={msg.startsWith('✅') ? 'dsr-msg-ok' : 'dsr-error'}>{msg}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>取消</button>
          <button
            className="btn btn-brand"
            onClick={handleSubmit}
            disabled={submitLoading || !parseResult || !url.trim()}
          >
            {submitLoading ? '⏳ 添加中...' : '立即添加'}
          </button>
        </div>
      </div>
    </div>
  );
}
